import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  BlockOutlined,
  FileExcelOutlined,
  FolderOutlined,
  SlidersOutlined,
  TableOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'
import './Settings.css'

type ChunkStrategy = {
  id: string
  label: string
  description: string
  enabled: boolean
}

function isFormValidationError(e: unknown): boolean {
  return !!e && typeof e === 'object' && 'errorFields' in e
}

export default function Settings() {
  const location = useLocation()
  const [chunkForm] = Form.useForm()
  const [chunkLoading, setChunkLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setChunkLoading(true)
    adminApi
      .get('/admin/settings/chunk')
      .then((r) => {
        if (!cancelled) chunkForm.setFieldsValue(r.data)
      })
      .catch(() => {
        if (!cancelled) message.error('加载切片配置失败')
      })
      .finally(() => {
        if (!cancelled) setChunkLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [location.pathname, chunkForm])

  const strategies: ChunkStrategy[] = Form.useWatch('strategies', chunkForm) || []
  const defaultStrategy: string = Form.useWatch('default_strategy', chunkForm) || 'fixed'
  const parentChunkMinChars: number = Form.useWatch('parent_chunk_min_chars', chunkForm) ?? 1400
  const chunkSize: number = Form.useWatch('chunk_size', chunkForm) ?? 500
  const tableParentMaxChars: number = Form.useWatch('table_parent_max_chars', chunkForm) ?? 0

  const parentSizeHint = Math.max(chunkSize * 3, parentChunkMinChars)
  const tableParentMaxHint =
    tableParentMaxChars > 0 ? tableParentMaxChars : parentSizeHint * 2

  const setDefaultStrategy = (id: string) => {
    chunkForm.setFieldValue('default_strategy', id)
  }

  const saveChunkWithCheck = async () => {
    try {
      const v = await chunkForm.validateFields()
      const enabled = ((v.strategies as ChunkStrategy[]) || []).filter((s) => s.enabled)
      if (!enabled.length) {
        message.warning('请至少启用一种切片方式')
        return
      }
      if (!enabled.some((s) => s.id === v.default_strategy)) {
        v.default_strategy = enabled[0].id
        chunkForm.setFieldValue('default_strategy', enabled[0].id)
      }
      await adminApi.put('/admin/settings/chunk', v)
      message.success('切片配置已保存')
    } catch (e: unknown) {
      if (isFormValidationError(e)) return
      message.error(e instanceof Error ? e.message : '保存失败')
    }
  }

  return (
    <div className="chunk-settings">
      <PageHeader
        title="切片管理"
        description="配置文档切块策略、表格切片与上传默认方式，属于「知识加工」流水线的前置环节。大语言模型与向量模型请在「模型管理」配置；索引语境增强请在「问答设置 → 索引语境」。"
      />

      <Form
        form={chunkForm}
        layout="vertical"
        className="chunk-settings-form"
        disabled={chunkLoading}
      >
        <Card
          className="chunk-card"
          title={
            <Space>
              <SlidersOutlined />
              通用参数
            </Space>
          }
        >
          <Typography.Text type="secondary" className="chunk-card-desc">
            适用于 Word、PDF、纯文本等策略；修改后仅影响重新索引的文档。
          </Typography.Text>
          <div className="chunk-settings-grid">
            <Form.Item
              name="max_upload_mb"
              label="单文件上传大小上限（MB）"
              rules={[{ required: true }]}
              extra="PDF / Word / Excel / 图片等附件的单文件大小限制。"
            >
              <InputNumber min={1} max={500} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="chunk_size"
              label="默认块大小（字符）"
              rules={[{ required: true }]}
              extra="子块检索粒度；也影响父子块中父块的下限计算。"
            >
              <InputNumber min={100} max={4000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="chunk_overlap"
              label="块重叠（字符）"
              rules={[{ required: true }]}
              extra="固定窗口切分时，相邻块在边界处重复的字数。"
            >
              <InputNumber min={0} max={500} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="parent_chunk_min_chars"
              label="父块最小字符数"
              rules={[{ required: true }]}
              extra={`父块大小 = max(子块×3, 本值)。当前约 ${parentSizeHint} 字。`}
            >
              <InputNumber min={300} max={8000} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Card>

        <Card
          className="chunk-card"
          title={
            <Space>
              <FolderOutlined />
              知识库目录
            </Space>
          }
        >
          <Typography.Text type="secondary" className="chunk-card-desc">
            限制左侧文件夹树的最大嵌套层数（根下第一层计为 1）。调高后可在更深层级建夹；调低不会自动合并已有深层目录，但无法在更深层继续新建。
          </Typography.Text>
          <Form.Item
            name="folder_max_depth"
            label="文件夹最大层级"
            rules={[{ required: true }]}
            extra="默认 8 级。上传文件夹时按相对路径自动建夹，同样受此限制。"
            style={{ maxWidth: 360, marginBottom: 0 }}
          >
            <InputNumber min={1} max={32} style={{ width: '100%' }} />
          </Form.Item>
        </Card>

        <Card
          className="chunk-card"
          title={
            <Space>
              <FileExcelOutlined />
              Excel / 表格切片
            </Space>
          }
        >
          <Typography.Text type="secondary" className="chunk-card-desc">
            适用于 Excel（.xls / .xlsx）及自动识别为表格的正文。上传/索引时先按「每表读取行数上限」抽取正文，再按行数或字符数切块；每块均带工作表名标记。
          </Typography.Text>
          <Form.Item
            name="spreadsheet_max_rows_per_sheet"
            label="每工作表最多读取行数"
            rules={[{ required: true }]}
            extra="抽取 Excel 正文时的行数上限（含表头）。超出部分截断并提示；调高后可索引更大表格，但解析与向量化更耗时。修改后需对已有条目重新索引。"
            style={{ marginBottom: 20 }}
          >
            <InputNumber min={100} max={200000} step={500} style={{ width: '100%' }} />
          </Form.Item>
          <div className="chunk-table-params">
            <div className="chunk-table-group">
              <Typography.Text strong className="chunk-table-group-title">
                <TableOutlined style={{ marginRight: 6 }} />
                子块（用于向量检索）
              </Typography.Text>
              <Form.Item
                name="table_child_rows_per_chunk"
                label="每块最多行数"
                rules={[{ required: true }]}
              >
                <InputNumber min={5} max={500} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="table_child_max_chars"
                label="每块最多字符"
                rules={[{ required: true }]}
                extra="行很宽时可能先于行数触顶。"
              >
                <InputNumber min={500} max={20000} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <div className="chunk-table-group">
              <Typography.Text strong className="chunk-table-group-title">
                <BlockOutlined style={{ marginRight: 6 }} />
                父块（扩展语境）
              </Typography.Text>
              <Form.Item
                name="table_parent_rows_per_chunk"
                label="每块最多行数"
                rules={[{ required: true }]}
              >
                <InputNumber min={10} max={2000} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="table_parent_max_chars"
                label="每块最多字符"
                rules={[{ required: true }]}
                extra={
                  tableParentMaxChars > 0
                    ? `当前生效上限：${tableParentMaxHint} 字。`
                    : `填 0 表示自动 = 父块字符上限×2（当前约 ${tableParentMaxHint} 字）。`
                }
              >
                <InputNumber min={0} max={50000} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          </div>
        </Card>

        <Card
          className="chunk-card"
          title={
            <Space>
              <BlockOutlined />
              切片策略
            </Space>
          }
        >
          <Form.Item name="default_strategy" hidden>
            <Input />
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            系统内置算法（编码不可改）。可改显示名称、停用某种方式，并用「默认」指定上传时的初始选项；推荐默认「自动」。
          </Typography.Paragraph>
          <Form.Item name="strategies">
            <Form.List name="strategies">
              {(fields) => (
                <Table
                  pagination={false}
                  size="small"
                  rowKey="key"
                  dataSource={fields}
                  columns={[
                    {
                      title: '默认',
                      width: 72,
                      render: (_, field) => {
                        const id = strategies[field.name]?.id
                        return (
                          <Radio
                            checked={defaultStrategy === id}
                            onChange={() => setDefaultStrategy(id)}
                          />
                        )
                      },
                    },
                    {
                      title: '启用',
                      width: 72,
                      render: (_, field) => (
                        <Form.Item
                          name={[field.name, 'enabled']}
                          valuePropName="checked"
                          noStyle
                        >
                          <Switch
                            onChange={(checked) => {
                              const id = strategies[field.name]?.id
                              if (!checked && id === defaultStrategy) {
                                const next = strategies.find(
                                  (s, i) => i !== field.name && s.enabled,
                                )
                                if (next) setDefaultStrategy(next.id)
                              }
                            }}
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '显示名称',
                      render: (_, field) => (
                        <Form.Item name={[field.name, 'label']} noStyle>
                          <Input />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '说明',
                      render: (_, field) => (
                        <Form.Item name={[field.name, 'description']} noStyle>
                          <Input variant="borderless" readOnly />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '编码',
                      width: 100,
                      render: (_, field) => (
                        <Form.Item name={[field.name, 'id']} noStyle>
                          <Input disabled variant="borderless" />
                        </Form.Item>
                      ),
                    },
                  ]}
                />
              )}
            </Form.List>
          </Form.Item>
        </Card>

        <div className="chunk-settings-actions">
          <Button type="primary" htmlType="button" onClick={saveChunkWithCheck}>
            保存切片配置
          </Button>
        </div>
      </Form>
    </div>
  )
}
